import { useState } from 'react';

export default function CompactFileDropzone({
  disabled,
  accept,
  hint,
  check,
  onFile,
  className = 'mt-2',
  compact = false,
}: {
  disabled: boolean;
  accept: string;
  hint: string;
  check: (file: File) => string | null;
  onFile: (file: File) => void;
  className?: string;
  /** Short row used by every closing upload, instead of a tall drop area. */
  compact?: boolean;
}) {
  const [over, setOver] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  function take(file: File | undefined) {
    if (!file || disabled) return;
    const nextProblem = check(file);
    if (nextProblem) {
      setProblem(nextProblem);
      return;
    }
    setProblem(null);
    onFile(file);
  }

  return (
    <label
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setOver(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        take(event.dataTransfer.files?.[0]);
      }}
      className={`${className} flex items-center justify-center rounded-xl border text-center ${
        compact ? 'flex-row flex-wrap gap-x-2 gap-y-0.5 px-3 py-2' : 'flex-col px-3 py-5'
      } ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      } ${over ? 'border-solid border-gray-900 bg-white dark:border-white dark:bg-gray-600' : 'border-dashed border-gray-400 bg-white/70 dark:border-white/20 dark:bg-gray-700'}`}
    >
      <input
        className="sr-only"
        type="file"
        accept={accept}
        disabled={disabled}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          take(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      <span className="text-sm font-medium text-gray-900 dark:text-white">Upload file</span>
      <span className={`text-xs text-gray-500 dark:text-gray-400 ${compact ? '' : 'mt-1'}`}>{hint}</span>
      {problem ? <span className="mt-1 text-sm text-rose-700 dark:text-rose-300">{problem}</span> : null}
    </label>
  );
}
